import { Box, Card, CardActionArea, CardContent, CardMedia, Chip, Grid, Typography } from '@mui/material';
import { useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { api } from '../../api/apiClient';
import { usePageMeta } from '../../hooks/usePageMeta';
import { SubscribeCard } from '../../components/SubscribeCard';

interface BlogSummary {
    blogId: number;
    title: string;
    imageUrl: string | null;
    shortDescription: string;
    datePosted: string | null;
    createdAt: string;
    tags: string[];
}

const BLOG_DOMAIN = 'General';

export function BlogListPage() {
    usePageMeta({
        title: 'Articles',
        description: 'Articles and insights from ColbrenSolutions on AI automation, small business software, and practical technology for growing teams.',
    });

    const navigate = useNavigate();
    const [blogs, setBlogs] = useState<BlogSummary[]>([]);
    const [loading, setLoading] = useState(true);
    const [error, setError] = useState<string | null>(null);

    useEffect(() => {
        api.get(`/api/blogs?domain=${BLOG_DOMAIN}`)
            .then(r => r.json())
            .then((data: BlogSummary[]) => setBlogs(data))
            .catch(() => setError('Failed to load articles'))
            .finally(() => setLoading(false));
    }, []);

    return (
        <>
            <Box sx={{ mb: 4 }}>
                <Typography variant='h3' sx={{ fontWeight: 700, mb: 1 }}>Articles</Typography>
                <Typography variant='body1' color='text.secondary'>
                    Practical articles on AI, automation, and real-world software solutions for small businesses.
                </Typography>
            </Box>

            <SubscribeCard domain={BLOG_DOMAIN} appName='Colbren Solutions UI' />

            {loading && <Typography color='text.secondary'>Loading…</Typography>}
            {error && <Typography color='error'>{error}</Typography>}

            {!loading && !error && blogs.length === 0 && (
                <Typography color='text.secondary'>No posts yet — check back soon.</Typography>
            )}

            <Grid container spacing={3}>
                {blogs.map(blog => (
                    <Grid item xs={12} sm={6} md={4} key={blog.blogId}>
                        <Card sx={{ height: '100%', display: 'flex', flexDirection: 'column', boxShadow: 'none', border: '1px solid #e0e0e0' }}>
                            <CardActionArea onClick={() => navigate(`/blog/${blog.blogId}`)} sx={{ flexGrow: 1, display: 'flex', flexDirection: 'column', alignItems: 'stretch' }}>
                                {blog.imageUrl && (
                                    <CardMedia component='img' image={blog.imageUrl} alt={blog.title} sx={{ width: '100%', aspectRatio: '16/9', objectFit: 'cover' }} />
                                )}
                                <CardContent sx={{ flexGrow: 1 }}>
                                    <Typography variant='caption' color='text.secondary'>
                                        {new Date(blog.datePosted ? blog.datePosted + 'T00:00:00' : blog.createdAt).toLocaleDateString('en-US', { year: 'numeric', month: 'long', day: 'numeric' })}
                                    </Typography>
                                    <Typography variant='h6' sx={{ fontWeight: 600, mt: 0.5, mb: 1 }}>{blog.title}</Typography>
                                    <Typography variant='body2' color='text.secondary' sx={{ mb: 1.5 }}>
                                        {blog.shortDescription}
                                    </Typography>
                                    {blog.tags.length > 0 && (
                                        <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 0.5 }}>
                                            {blog.tags.map(tag => (
                                                <Chip key={tag} label={`#${tag}`} size='small' variant='outlined' sx={{ fontSize: '0.7rem' }} />
                                            ))}
                                        </Box>
                                    )}
                                </CardContent>
                            </CardActionArea>
                        </Card>
                    </Grid>
                ))}
            </Grid>
        </>
    );
}
